import { AnimatePresence, motion } from 'framer-motion';
import { BookOpen, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import livretModules, { getModuleById } from './livretData.js';

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM (aligné avec VisualizationPostures)
// ═══════════════════════════════════════════════════════════════════════════════

const C = {
  bg: '#06060f',
  bgPanel: '#0a0a18',
  bgCard: '#0d0d1e',
  bgInset: '#080814',
  border: 'rgba(167, 139, 250, 0.10)',
  violet: '#a78bfa',
  violetFaint: 'rgba(167, 139, 250, 0.08)',
  cyan: '#22d3ee',
  textPrimary: '#e2ecff',
  textSecond: '#8892b0',
  textMuted: '#4a5578',
};

const MONO = '"JetBrains Mono", "Fira Code", monospace';
const DISPLAY = '"Syne", "Space Grotesk", sans-serif';
const BODY = '"Inter", system-ui, sans-serif';

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL — Livret
// ═══════════════════════════════════════════════════════════════════════════════

export const Livret = ({
  open = false,
  onClose,
  initialModuleId = '00',
  profile = 'apprenant',
}) => {
  const [moduleId, setModuleId] = useState(initialModuleId);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Synchroniser si le parent force un module
  useEffect(() => {
    if (open && initialModuleId) {
      setModuleId(initialModuleId);
    }
  }, [open, initialModuleId]);

  // Filtrer les modules selon le profil (mais garder accessibles les modules transversaux)
  const availableModules = useMemo(() => {
    return livretModules.filter(m =>
      m.profiles?.includes(profile) ||
      m.profiles?.includes('apprenant') ||
      m.id === '00'
    );
  }, [profile]);

  const currentModule = useMemo(
    () => getModuleById(moduleId) || availableModules[0],
    [moduleId, availableModules]
  );

  const currentIndex = useMemo(
    () => availableModules.findIndex(m => m.id === currentModule?.id),
    [availableModules, currentModule]
  );

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < availableModules.length - 1;

  const goPrev = () => {
    if (hasPrev) setModuleId(availableModules[currentIndex - 1].id);
  };

  const goNext = () => {
    if (hasNext) setModuleId(availableModules[currentIndex + 1].id);
  };

  // Navigation clavier
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, currentIndex, availableModules]);

  if (!currentModule) return null;
  if (!mounted) return null;

  const Content = currentModule.content;

  return createPortal(
    <AnimatePresence>
      {open && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(6, 6, 15, 0.7)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
          padding: 20,
          boxSizing: 'border-box',
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >

          {/* Modale */}
          <motion.div
          initial={{ opacity: 0, scale: 0.97, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: 10 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            maxWidth: 780,
            height: '100%',
            maxHeight: 720,
            background: C.bgPanel,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          }}
        >
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 20px',
              borderBottom: `1px solid ${C.border}`,
              background: C.bgCard,
              flexShrink: 0,
            }}>
              <div className="flex items-center gap-3">
                <BookOpen size={14} color={C.violet} />
                <div>
                  <div style={{
                    fontFamily: MONO,
                    fontSize: 9,
                    color: C.violet,
                    letterSpacing: '0.2em',
                  }}>
                    LIVRET · MODULE {currentModule.id}
                  </div>
                  <div style={{
                    fontFamily: DISPLAY,
                    fontSize: 13,
                    color: C.textPrimary,
                    fontWeight: 500,
                    marginTop: 1,
                  }}>
                    {currentModule.title}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {currentModule.duration && (
                  <span style={{
                    fontFamily: MONO,
                    fontSize: 9,
                    color: C.textMuted,
                    padding: '3px 8px',
                    background: C.bgInset,
                    border: `1px solid ${C.border}`,
                    borderRadius: 3,
                    letterSpacing: '0.05em',
                  }}>
                    {currentModule.duration}
                  </span>
                )}

                <button
                  onClick={onClose}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 26,
                    height: 26,
                    background: 'transparent',
                    border: `1px solid ${C.border}`,
                    borderRadius: 4,
                    cursor: 'pointer',
                    color: C.textMuted,
                  }}
                >
                  <X size={12} />
                </button>
              </div>
            </div>

            {/* Sommaire (barre horizontale) */}
            <div style={{
              display: 'flex',
              gap: 2,
              padding: '6px 20px',
              borderBottom: `1px solid ${C.border}`,
              background: C.bg,
              flexShrink: 0,
              overflowX: 'auto',
              scrollbarWidth: 'none',
            }}>
              {availableModules.map(m => {
                const active = m.id === currentModule.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => setModuleId(m.id)}
                    title={m.title}
                    style={{
                      flexShrink: 0,
                      padding: '4px 10px',
                      background: active ? C.violetFaint : 'transparent',
                      border: `1px solid ${active ? C.violet + '60' : 'transparent'}`,
                      borderRadius: 3,
                      cursor: 'pointer',
                      color: active ? C.violet : C.textMuted,
                      fontFamily: MONO,
                      fontSize: 9,
                      letterSpacing: '0.1em',
                      transition: 'all 0.15s',
                    }}
                  >
                    {m.id}
                  </button>
                );
              })}
            </div>

            {/* Contenu scrollable */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px 40px 24px 40px',
              scrollbarWidth: 'thin',
            }}>
              <div style={{ maxWidth: 640, margin: '0 auto' }}>
                {currentModule.subtitle && (
                  <div style={{
                    fontFamily: BODY,
                    fontSize: 12,
                    color: C.textMuted,
                    marginBottom: 20,
                    paddingBottom: 16,
                    borderBottom: `1px solid ${C.border}`,
                    fontStyle: 'italic',
                  }}>
                    {currentModule.subtitle}
                  </div>
                )}
                <Content />
              </div>
            </div>

            {/* Navigation */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 20px',
              borderTop: `1px solid ${C.border}`,
              background: C.bgCard,
              flexShrink: 0,
            }}>
              <button
                onClick={goPrev}
                disabled={!hasPrev}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  background: hasPrev ? C.bgInset : 'transparent',
                  border: `1px solid ${hasPrev ? C.border : 'transparent'}`,
                  borderRadius: 3,
                  cursor: hasPrev ? 'pointer' : 'not-allowed',
                  color: hasPrev ? C.textSecond : C.textMuted + '40',
                  fontFamily: MONO,
                  fontSize: 9,
                  letterSpacing: '0.1em',
                }}
              >
                <ChevronLeft size={12} />
                PRÉCÉDENT
              </button>

              <span style={{
                fontFamily: MONO,
                fontSize: 9,
                color: C.textMuted,
                letterSpacing: '0.1em',
              }}>
                {currentIndex + 1} / {availableModules.length}
              </span>

              <button
                onClick={goNext}
                disabled={!hasNext}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  background: hasNext ? C.bgInset : 'transparent',
                  border: `1px solid ${hasNext ? C.border : 'transparent'}`,
                  borderRadius: 3,
                  cursor: hasNext ? 'pointer' : 'not-allowed',
                  color: hasNext ? C.textSecond : C.textMuted + '40',
                  fontFamily: MONO,
                  fontSize: 9,
                  letterSpacing: '0.1em',
                }}
              >
                SUIVANT
                <ChevronRight size={12} />
              </button>
            </div>
          </motion.div>
          </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default Livret;